import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaMinus, FaPlus, FaTimes } from 'react-icons/fa';
import userService from '../../services/userService';

const VIEW_SIZE = 280;
const OUTPUT_SIZE = 512;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 12000;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: max(0.75rem, env(safe-area-inset-top)) 0.75rem max(0.75rem, env(safe-area-inset-bottom));
    align-items: flex-end;
  }
`;

const Panel = styled.div`
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  padding: 1.25rem 1.25rem 1.35rem;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: min(92dvh, 640px);
    overflow: auto;
    padding: 1rem 1rem 1.15rem;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;

  h2 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;

    h2 {
      font-size: 1rem;
    }
  }
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
  background: var(--color-light);
  color: var(--color-text-primary);
  cursor: pointer;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
  }
`;

const PreviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1rem;
`;

const CircleStage = styled.div`
  width: min(${VIEW_SIZE}px, 100%);
  aspect-ratio: 1;
  height: auto;
  border-radius: 50%;
  border: 2px solid var(--color-border-primary);
  background: var(--color-bg-tertiary);
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  touch-action: none;
  cursor: ${({ $editing }) => ($editing ? 'grab' : 'default')};
  user-select: none;

  &:active {
    cursor: ${({ $editing }) => ($editing ? 'grabbing' : 'default')};
  }

  @media (max-width: 768px) {
    width: min(200px, 58vw);
  }
`;

const StageImage = styled.img`
  position: absolute;
  left: 0;
  top: 0;
  max-width: none;
  pointer-events: none;
  transform-origin: 0 0;
`;

const Fallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  font-weight: 700;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  user-select: none;

  @media (max-width: 768px) {
    font-size: 2.75rem;
  }
`;

const ZoomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  max-width: ${VIEW_SIZE}px;

  @media (max-width: 768px) {
    max-width: min(200px, 58vw);
  }
`;

const ZoomButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ZoomSlider = styled.input`
  flex: 1;
  accent-color: var(--color-text-secondary);
`;

const ActionsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.65rem 0.5rem;
  border-radius: 10px;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  font-size: 0.82rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  &:hover:not(:disabled) {
    background: var(--color-light);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    min-height: 2.75rem;
    padding: 0.55rem 0.75rem;
    font-size: 0.9rem;
  }

  ${({ $danger }) =>
    $danger &&
    `
    color: #c0392b;
    border-color: rgba(192, 57, 43, 0.35);
  `}

  ${({ $primary }) =>
    $primary &&
    `
    background: var(--color-btn-primary-bg, #28a745);
    color: var(--color-btn-primary-text, #fff);
    border-color: var(--color-btn-primary-border, #28a745);
  `}
`;

const EditActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  text-align: center;
`;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if ((img.naturalWidth || img.width) > 0) resolve(img);
      else reject(new Error('Imagem sem dimensões'));
    };
    img.onerror = () => reject(new Error('Falha ao carregar imagem'));
    img.src = src;
  });

/**
 * Obtém data URL local da foto (nunca URL remota "tainted").
 * Foto salva: GET /api/private/user/current/photo → imageBase64.
 */
const resolveCleanDataUrl = async (url) => {
  const src = String(url || '');
  if (!src) throw new Error('URL vazia');
  if (src.startsWith('data:')) return src;

  if (src.startsWith('blob:')) {
    const res = await fetch(src);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Foto remota (photoUrl): busca bytes via API autenticada
  try {
    return await userService.getCurrentPhoto();
  } catch (err) {
    console.error(err);
    throw new Error('CLEAN_SRC_UNAVAILABLE');
  }
};

const dataUrlToBase64 = (dataUrl) => {
  const str = String(dataUrl || '');
  return str.includes(',') ? str.split(',')[1] : str;
};

const ProfilePhotoModal = ({
  open,
  onClose,
  photoUrl,
  initialName = 'U',
  onSaved,
  addToast,
}) => {
  const fileInputRef = useRef(null);
  const stageRef = useRef(null);
  const dragRef = useRef(null);

  const [mode, setMode] = useState('view'); // view | edit
  const [busy, setBusy] = useState(false);
  const [sourceUrl, setSourceUrl] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [stagePx, setStagePx] = useState(VIEW_SIZE);
  const objectUrlRef = useRef(null);

  const avatarInitial = (initialName || 'U').trim().charAt(0) || 'U';
  const hasSavedPhoto = Boolean(photoUrl);
  const hasSource = Boolean(sourceUrl);

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const resetEditorState = useCallback(() => {
    revokeObjectUrl();
    setMode('view');
    setSourceUrl(null);
    setNaturalSize({ w: 0, h: 0 });
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [revokeObjectUrl]);

  useEffect(() => {
    if (!open) {
      resetEditorState();
      setBusy(false);
    }
  }, [open, resetEditorState]);

  useEffect(() => () => revokeObjectUrl(), [revokeObjectUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, busy, onClose]);

  useEffect(() => {
    if (!open || !stageRef.current) return undefined;
    const el = stageRef.current;
    const update = () => {
      const size = el.getBoundingClientRect().width || VIEW_SIZE;
      setStagePx(size);
    };
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [open, mode, sourceUrl]);

  const baseScale =
    naturalSize.w > 0 && naturalSize.h > 0
      ? Math.max(stagePx / naturalSize.w, stagePx / naturalSize.h)
      : 1;
  const displayScale = baseScale * zoom;
  const dispW = naturalSize.w * displayScale;
  const dispH = naturalSize.h * displayScale;

  const clampPan = useCallback(
    (x, y, nextZoom = zoom) => {
      if (!naturalSize.w || !naturalSize.h) return { x: 0, y: 0 };
      const scale = Math.max(stagePx / naturalSize.w, stagePx / naturalSize.h) * nextZoom;
      const w = naturalSize.w * scale;
      const h = naturalSize.h * scale;
      const minX = stagePx - w;
      const minY = stagePx - h;
      return {
        x: Math.min(0, Math.max(minX, x)),
        y: Math.min(0, Math.max(minY, y)),
      };
    },
    [naturalSize, stagePx, zoom]
  );

  const startEditingWithUrl = async (url) => {
    try {
      setBusy(true);

      let cleanSrc;
      try {
        cleanSrc = await resolveCleanDataUrl(url);
      } catch (resolveErr) {
        if (resolveErr?.message === 'CLEAN_SRC_UNAVAILABLE') {
          addToast?.(
            'Não foi possível carregar a foto atual para edição. Use “Trocar foto”.',
            'error'
          );
          return;
        }
        throw resolveErr;
      }

      revokeObjectUrl();

      const img = await loadImage(cleanSrc);
      const nw = img.naturalWidth || img.width;
      const nh = img.naturalHeight || img.height;
      if (!nw || !nh) {
        throw new Error('Dimensões inválidas');
      }

      const measuredStage =
        stageRef.current?.getBoundingClientRect()?.width || stagePx || VIEW_SIZE;
      setStagePx(measuredStage);

      setNaturalSize({ w: nw, h: nh });
      setSourceUrl(cleanSrc);
      setZoom(1);
      const scale = Math.max(measuredStage / nw, measuredStage / nh);
      setPan({
        x: (measuredStage - nw * scale) / 2,
        y: (measuredStage - nh * scale) / 2,
      });
      setMode('edit');
    } catch (err) {
      console.error(err);
      addToast?.('Não foi possível carregar a imagem para edição.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = async () => {
    if (!photoUrl || busy) return;
    await startEditingWithUrl(photoUrl);
  };

  const handleChangeClick = () => {
    if (busy) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      addToast?.('Selecione um arquivo de imagem.', 'error');
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      await startEditingWithUrl(dataUrl);
    } catch (err) {
      console.error(err);
      addToast?.('Não foi possível ler o arquivo.', 'error');
    }
  };

  const handleCancelEdit = () => {
    resetEditorState();
  };

  const handleZoomChange = (nextZoom) => {
    const z = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoom));
    const scaleBefore = baseScale * zoom;
    const scaleAfter = baseScale * z;
    const centerX = stagePx / 2;
    const centerY = stagePx / 2;
    const imgX = (centerX - pan.x) / scaleBefore;
    const imgY = (centerY - pan.y) / scaleBefore;
    const nextPan = clampPan(centerX - imgX * scaleAfter, centerY - imgY * scaleAfter, z);
    setZoom(z);
    setPan(nextPan);
  };

  const onPointerDown = (e) => {
    if (mode !== 'edit' || busy) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: pan.x,
      originY: pan.y,
    };
  };

  const onPointerMove = (e) => {
    if (!dragRef.current || mode !== 'edit') return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan(
      clampPan(dragRef.current.originX + dx, dragRef.current.originY + dy)
    );
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const exportCroppedBase64 = async () => {
    let workingSrc = sourceUrl;
    const isLocal =
      String(workingSrc || '').startsWith('data:') ||
      String(workingSrc || '').startsWith('blob:');

    if (!isLocal) {
      workingSrc = await resolveCleanDataUrl(workingSrc);
      setSourceUrl(workingSrc);
    }

    const img = await loadImage(workingSrc);
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');

    const ratio = OUTPUT_SIZE / stagePx;
    ctx.save();
    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.drawImage(img, pan.x * ratio, pan.y * ratio, dispW * ratio, dispH * ratio);
    ctx.restore();

    try {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      return dataUrlToBase64(dataUrl);
    } catch (err) {
      console.error(err);
      throw new Error(
        'Não foi possível exportar o recorte. Use “Trocar foto” e selecione o arquivo de novo.'
      );
    }
  };

  const handleSave = async () => {
    if (!sourceUrl || busy) return;
    try {
      setBusy(true);
      const base64 = await exportCroppedBase64();
      await userService.updateCurrentPhoto(base64);
      await onSaved?.();
      addToast?.('Foto de perfil atualizada.', 'success');
      resetEditorState();
      onClose?.();
    } catch (err) {
      console.error(err);
      addToast?.(
        err?.message ||
          err?.response?.data?.message ||
          'Não foi possível atualizar a foto de perfil.',
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!hasSavedPhoto || busy) return;
    const ok = window.confirm('Remover a foto de perfil?');
    if (!ok) return;
    try {
      setBusy(true);
      await userService.removeCurrentPhoto();
      await onSaved?.();
      addToast?.('Foto de perfil removida.', 'success');
      resetEditorState();
      onClose?.();
    } catch (err) {
      console.error(err);
      addToast?.(
        err?.response?.data?.message || 'Não foi possível remover a foto de perfil.',
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  if (!open || typeof document === 'undefined') return null;

  const showEditorImage = mode === 'edit' && hasSource;
  const showSavedPreview = mode === 'view' && hasSavedPhoto;
  const showFallback = !showEditorImage && !showSavedPreview;

  return createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label="Foto de perfil"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
    >
      <Panel>
        <Header>
          <h2>Foto de perfil</h2>
          <CloseButton type="button" onClick={() => !busy && onClose?.()} aria-label="Fechar">
            <FaTimes />
          </CloseButton>
        </Header>

        <PreviewWrap>
          <CircleStage
            ref={stageRef}
            $editing={mode === 'edit'}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {showEditorImage && (
              <StageImage
                src={sourceUrl}
                alt="Ajuste da foto"
                draggable={false}
                style={{
                  width: dispW,
                  height: dispH,
                  transform: `translate(${pan.x}px, ${pan.y}px)`,
                }}
              />
            )}
            {showSavedPreview && (
              <StageImage
                src={photoUrl}
                alt="Foto de perfil"
                draggable={false}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  inset: 0,
                  transform: 'none',
                }}
              />
            )}
            {showFallback && <Fallback>{avatarInitial}</Fallback>}
          </CircleStage>

          {mode === 'edit' && (
            <>
              <Hint>Arraste para posicionar e use o zoom para enquadrar</Hint>
              <ZoomRow>
                <ZoomButton
                  type="button"
                  aria-label="Diminuir zoom"
                  disabled={busy || zoom <= MIN_ZOOM}
                  onClick={() => handleZoomChange(zoom - 0.1)}
                >
                  <FaMinus />
                </ZoomButton>
                <ZoomSlider
                  type="range"
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={0.01}
                  value={zoom}
                  disabled={busy}
                  onChange={(e) => handleZoomChange(Number(e.target.value))}
                  aria-label="Zoom da foto"
                />
                <ZoomButton
                  type="button"
                  aria-label="Aumentar zoom"
                  disabled={busy || zoom >= MAX_ZOOM}
                  onClick={() => handleZoomChange(zoom + 0.1)}
                >
                  <FaPlus />
                </ZoomButton>
              </ZoomRow>
            </>
          )}
        </PreviewWrap>

        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelected}
        />

        {mode === 'edit' ? (
          <EditActions>
            <ActionButton type="button" disabled={busy} onClick={handleCancelEdit}>
              Cancelar
            </ActionButton>
            <ActionButton type="button" $primary disabled={busy} onClick={handleSave}>
              {busy ? 'Salvando...' : 'Salvar'}
            </ActionButton>
          </EditActions>
        ) : (
          <ActionsRow>
            <ActionButton
              type="button"
              disabled={busy || !hasSavedPhoto}
              onClick={handleEdit}
            >
              Editar foto
            </ActionButton>
            <ActionButton type="button" disabled={busy} onClick={handleChangeClick}>
              Trocar foto
            </ActionButton>
            <ActionButton
              type="button"
              $danger
              disabled={busy || !hasSavedPhoto}
              onClick={handleRemove}
            >
              Remover foto
            </ActionButton>
          </ActionsRow>
        )}
      </Panel>
    </Overlay>,
    document.body
  );
};

ProfilePhotoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  photoUrl: PropTypes.string,
  initialName: PropTypes.string,
  onSaved: PropTypes.func,
  addToast: PropTypes.func,
};

export default ProfilePhotoModal;
